import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  IMPORT_DESCRIPTION_MAX_LENGTH,
  IMPORT_DESCRIPTION_MIN_LENGTH,
  IMPORT_DESCRIPTION_TOO_SHORT,
  type ImportSessionListEntry,
  type ImportTargetEvent,
} from '@ni/shared';
import { useCreateImport, useImports } from '../api/hooks';
import { loadErrorLine } from '../api/errors';
import {
  CANCEL,
  IMPORTS_EMPTY,
  IMPORTS_NEW,
  IMPORTS_NEW_WORKING,
  IMPORTS_TABLE_HEADS,
  IMPORTS_TITLE,
  IMPORT_DESCRIPTION_LABEL,
  IMPORT_DESCRIPTION_PLACEHOLDER,
  IMPORT_FILE_ACCEPT,
  IMPORT_FILE_HINT,
  IMPORT_FILE_LABEL,
  IMPORT_INTO_EVENT,
  IMPORT_NEEDS_A_FILE,
  SOMEONE_HERE,
  characterCount,
  importFileTooBig,
  importLedgerRow,
  EVENT_ATTENDEES_UPLOAD_ADD,
  EVENT_ATTENDEES_UPLOAD_ADD_HINT,
  EVENT_ATTENDEES_UPLOAD_MODE,
  EVENT_ATTENDEES_UPLOAD_REPLACE,
  EVENT_ATTENDEES_UPLOAD_REPLACE_HINT,
} from '../lib/copy';
import { formatDay } from '../lib/format';
import { ErrorLine } from '../components/ui/error-line';
import { FilePicker } from '../components/imports/file-picker';
import { Modal } from '../components/ui/modal';
import { LinkRow } from '../components/ui/row-link';
import { WorkingButton } from '../components/ui/working-button';
import { Loading } from '../components/ui/loading';
import { EmptyLine } from '../components/ui/empty-line';
import { Button } from '../components/ui/button';
import { Field } from '../components/ui/field';
import './imports.css';

/**
 * **The Imports ledger** (import note §1.1, §2.4) — one row per session this org
 * has opened, and one action at the head.
 *
 * A session is a place rather than a job: it is rows in a table, nothing runs
 * between turns, and the office comes back to one the way they come back to a
 * record. So this is a list of documents, not a queue, and the only thing that
 * moves under the reader is a session that is mid-flight — which is what the
 * ordinary sixty-second read is for.
 *
 * Split the way the People screen is: the route owns the reads and the writes,
 * the table and the form are pure, and either can be asserted without a DOM.
 */
export function Imports() {
  const imports = useImports();
  const [opening, setOpening] = useState(false);

  const entries = imports.data ?? [];

  return (
    <div className="column imports">
      <div className="head">
        <div className="titles">
          <div className="title">{IMPORTS_TITLE}</div>
        </div>
        <div className="actions-right">
          <Button variant="hero" onClick={() => setOpening(true)}>
            {IMPORTS_NEW}
          </Button>
        </div>
      </div>

      <div className="imports-list">
        {imports.isPending ? (
          <EmptyLine>
            <Loading />
          </EmptyLine>
        ) : entries.length === 0 ? (
          <EmptyLine>{imports.isError ? loadErrorLine(imports.error) : IMPORTS_EMPTY}</EmptyLine>
        ) : (
          <ImportsTable entries={entries} />
        )}
      </div>

      {opening ? <NewImport onClose={() => setOpening(false)} /> : null}
    </div>
  );
}

/**
 * **An attendee upload's way back to its event**, on its ledger row and its
 * session head. It sits among imports into the record, so it says which event
 * it is for rather than leaving that to the description.
 */
export function ImportEventBadge({ event }: { event: ImportTargetEvent }) {
  return (
    <Link
      className="event-badge"
      to={`/events/${event.entity_id}/attendees`}
      title={`${IMPORT_INTO_EVENT} ${event.display_name}`}
    >
      <span className="event-badge-kind">{IMPORT_INTO_EVENT}</span> {event.display_name}
    </Link>
  );
}

/**
 * The ledger table. Every state gets the same four columns and its own fifth —
 * see {@link importLedgerRow}, which is where that sentence is decided.
 */
export function ImportsTable({ entries }: { entries: ImportSessionListEntry[] }) {
  return (
    <table className="table record-table imports-table">
      <thead>
        {/* `scope="col"` on every one: a screen reader reading a cell announces
            the column it is under, and without the scope it has to guess which
            row of `thead` is the header row. Five words, and the whole table
            becomes navigable. */}
        <tr>
          <th scope="col">{IMPORTS_TABLE_HEADS.file}</th>
          <th scope="col">{IMPORTS_TABLE_HEADS.what}</th>
          <th scope="col">{IMPORTS_TABLE_HEADS.who}</th>
          <th scope="col">{IMPORTS_TABLE_HEADS.when}</th>
          <th scope="col">{IMPORTS_TABLE_HEADS.state}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((entry) => {
          const row = importLedgerRow(entry);
          return (
            <LinkRow key={entry.id} to={`/imports/${entry.id}`}>
              {/* The filename is the link, and the description is the cell
                  beside it — clamped to two lines, with the whole of it on the
                  cell's `title` and on the session's File tab. A board's
                  description runs to a paragraph and it was setting the height
                  of every row in the ledger. */}
              <td className="file">
                <Link className="import-file" to={`/imports/${entry.id}`}>
                  {entry.filename}
                </Link>
                {entry.target_event ? <ImportEventBadge event={entry.target_event} /> : null}
              </td>
              {/* The clamp is on a box inside the cell, not on the cell: a `td`
                  set to `-webkit-box` stops being a table cell and takes the
                  column's width with it. */}
              <td className="quiet-cell what">
                <div className="clamp-2" title={entry.description}>
                  {entry.description}
                </div>
              </td>
              <td className="quiet-cell who">{entry.created_by.name ?? SOMEONE_HERE}</td>
              <td className="quiet-cell when">{formatDay(entry.created_at)}</td>
              <td className="state">
                <span className="state-word">{row.state}</span>
                {row.detail ? <span className="state-detail">{row.detail}</span> : null}
              </td>
            </LinkRow>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * As much of {@link useCreateImport} as the door presses, so the door's one
 * promise can be held by a spec without a DOM: the upload lands, and the page
 * goes to the session it made. There is no second call here, which is the whole
 * of the fix — nothing on this path waits on the planner.
 */
export interface NewImportUpload {
  mutate: (
    variables: { file: File; description: string; eventId?: string; replaceList?: boolean },
    options: { onSuccess: (session: { id: string }) => void },
  ) => void;
}

export function openNewImport(
  create: NewImportUpload,
  navigate: (to: string) => void,
  fields: { file: File; description: string; eventId?: string; replaceList?: boolean },
): void {
  create.mutate(fields, { onSuccess: (session) => navigate(`/imports/${session.id}`) });
}

/** The description well's floor and its ceiling, in rows. */
const DESCRIPTION_ROWS = 4;
const DESCRIPTION_ROWS_MAX = 10;

/**
 * Grow the well to what is in it, four rows to ten.
 *
 * **Measured, not counted.** The office writes one long sentence far more often
 * than it writes ten short ones, so a row count that only saw newlines would
 * leave a wrapped paragraph scrolling exactly the way the single-line input did.
 * The height is cleared before `scrollHeight` is read, because a box already
 * taller than its content reports its own height and would never come back down.
 *
 * `box-sizing: border-box` is global, so the height being set has to cover the
 * padding `scrollHeight` already counts *and* the border it does not.
 *
 * It gives up rather than guessing when the box has no computed line height —
 * which is every non-browser renderer — and the `rows` attribute is left in
 * charge, so the well is four rows tall wherever this cannot run.
 */
function grow(node: HTMLTextAreaElement): void {
  const style = window.getComputedStyle(node);
  const line = Number.parseFloat(style.lineHeight);
  const pad = Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom);
  const border =
    Number.parseFloat(style.borderTopWidth) + Number.parseFloat(style.borderBottomWidth);
  if (!Number.isFinite(line) || !Number.isFinite(pad) || !Number.isFinite(border)) return;

  node.style.height = 'auto';
  const floor = DESCRIPTION_ROWS * line + pad + border;
  const ceiling = DESCRIPTION_ROWS_MAX * line + pad + border;
  node.style.height = `${Math.min(Math.max(node.scrollHeight + border, floor), ceiling)}px`;
}

/**
 * **New import**: the file and the line saying what it is, both required (§1.1).
 *
 * The description is the whole point of the door — the planner sees it and the
 * profile and nothing else — so it is refused short here as well as on the way
 * in, with the contract's own sentence rather than a second wording of it.
 *
 * **The door closes on the upload.** It used to run the planner's opening turn
 * before it navigated, so a gesture whose visible work took a few seconds held
 * the modal for up to two minutes — with the new session's row already on the
 * ledger behind it, which is a door plainly saying it is finished and refusing
 * to close. The upload is the whole of this form's promise now; the session page
 * owes the turn and says so in the conversation, where there is somewhere to put
 * a working line and a Retry.
 *
 * **The description is a well, not a line.** It was a single-line `<input>`,
 * which scrolls: past forty-odd characters the beginning of what the office had
 * written walked off the left edge, and the one field whose whole job is to be
 * read back before it is sent was the one field they could not read. It is a
 * textarea now, four rows to ten — and Enter therefore writes a newline rather
 * than submitting, so the button is the only way out of the form.
 */
/**
 * **New import**, or, with `eventId`, an attendee list for that event's graph
 * (event graph design §3a). The session page is the same either way.
 */
export function NewImport({
  onClose,
  eventId,
  title = IMPORTS_NEW,
  submitLabel = IMPORTS_NEW,
  placeholder = IMPORT_DESCRIPTION_PLACEHOLDER,
  offerReplace = false,
}: {
  onClose: () => void;
  eventId?: string;
  /** The event already has a list: ask whether this file adds to it or replaces it. */
  offerReplace?: boolean;
  title?: string;
  submitLabel?: string;
  placeholder?: string;
}) {
  const create = useCreateImport();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [touched, setTouched] = useState(false);
  const [replace, setReplace] = useState(false);

  const trimmed = description.trim();
  const tooShort = trimmed.length < IMPORT_DESCRIPTION_MIN_LENGTH;
  /* Refused only once they have tried: a form that is red before it has been
     filled in is telling the office off for not having finished typing. */
  const short = touched && tooShort;
  const countId = 'import-description-count';
  const shortId = 'import-description-short';
  const missingFile = file === null;
  /* **Asked before the file is sent.** The cap is the server's and it is
     enforced by the upload interceptor, which answers a bare 413 with no
     envelope on it — after the browser has spent a minute pushing forty
     megabytes up. The office is told the moment they pick the file, in a
     sentence that says what to do about it. */
  const tooBig = file === null ? null : importFileTooBig(file.size, file.name);

  const submit = () => {
    setTouched(true);
    if (missingFile || tooShort || tooBig !== null || create.isPending) return;
    openNewImport(create, navigate, {
      file,
      description: trimmed,
      ...(eventId ? { eventId } : {}),
      ...(eventId && offerReplace && replace ? { replaceList: true } : {}),
    });
  };

  return (
    <Modal label={title} className="new-import" onClose={onClose}>
      <div className="modal-head">{title}</div>

      <Field label={IMPORT_FILE_LABEL} htmlFor="import-file">
        <FilePicker
          id="import-file"
          accept={IMPORT_FILE_ACCEPT}
          disabled={create.isPending}
          chosen={file}
          hint={IMPORT_FILE_HINT}
          onChoose={setFile}
        />
        {touched && missingFile ? <div className="src">{IMPORT_NEEDS_A_FILE}</div> : null}
        {tooBig ? <div className="src">{tooBig}</div> : null}
      </Field>

      {eventId && offerReplace ? (
        <div className="modal-choice">
          <div role="radiogroup" aria-label={EVENT_ATTENDEES_UPLOAD_MODE}>
            {[
              {
                value: false,
                label: EVENT_ATTENDEES_UPLOAD_ADD,
                hint: EVENT_ATTENDEES_UPLOAD_ADD_HINT,
              },
              {
                value: true,
                label: EVENT_ATTENDEES_UPLOAD_REPLACE,
                hint: EVENT_ATTENDEES_UPLOAD_REPLACE_HINT,
              },
            ].map((mode) => (
              <label className="picker-option" key={mode.label}>
                <input
                  type="radio"
                  name="attendee-upload-mode"
                  checked={replace === mode.value}
                  disabled={create.isPending}
                  onChange={() => setReplace(mode.value)}
                />
                <span>
                  {mode.label}
                  <span className="src"> {mode.hint}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      ) : null}

      <Field label={IMPORT_DESCRIPTION_LABEL} htmlFor="import-description">
        {/* The count and the short-line are the field's own description, as they
            are on Settings: `aria-invalid` is what says this field is why the
            form will not go, and neither line is a live region, because a count
            that announced itself on every keystroke would be unusable to the
            reader it was added for. */}
        <textarea
          id="import-description"
          rows={DESCRIPTION_ROWS}
          maxLength={IMPORT_DESCRIPTION_MAX_LENGTH}
          placeholder={placeholder}
          value={description}
          disabled={create.isPending}
          aria-invalid={short || undefined}
          aria-describedby={short ? `${countId} ${shortId}` : countId}
          onChange={(event) => {
            setDescription(event.target.value);
            grow(event.currentTarget);
          }}
        />
        <div id={countId} className="char-count">
          {characterCount(trimmed.length, IMPORT_DESCRIPTION_MAX_LENGTH)}
        </div>
        {short ? (
          <div id={shortId} className="src">
            {IMPORT_DESCRIPTION_TOO_SHORT}
          </div>
        ) : null}
      </Field>

      <div className="modal-actions new-import-actions">
        <Button variant="act" onClick={onClose}>
          {CANCEL}
        </Button>
        <WorkingButton
          label={submitLabel}
          workingLabel={IMPORTS_NEW_WORKING}
          working={create.isPending}
          onClick={submit}
        />
      </div>

      {create.isError ? <ErrorLine error={create.error} /> : null}
    </Modal>
  );
}
